/**
 * AppRouter — define todas as rotas da SPA Nava Flow.
 *
 * F0-FE-2: todas as rotas (exceto /login e /auth/callback) são protegidas
 * por <AuthGuard>. O guard redireciona para /login se não autenticado,
 * exibe MfaScreen se status === 'mfa-required', e spinner durante restore.
 *
 * O NavigationProvider envolve toda a árvore roteada, expondo o shim
 * navigate(screen) compatível com o protótipo.
 */
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { NavaStyles } from '@/theme/NavaStyles';
import { AppLayout } from '@/components/layout/AppLayout';
import { NavigationProvider, useNavigationContext } from '@/navigation';
import { AuthGuard } from '@/auth/AuthGuard';
import GitOAuthCallback from '@/screens/GitOAuthCallback';
import SsoCallbackScreen from '@/screens/SsoCallbackScreen';
import {
  SplashScreen,
  LoginScreen,
  DashboardScreen,
  UsageScreen,
  AssistantScreen,
  SettingsScreen,
  AccessMatrixScreen,
  StepPromptsScreen,
  CatalogScreen,
  GraphViewScreen,
  PlaceholderScreen,
  ProjectHubScreen,
  DesignStudioScreen,
  BlueprintScreen,
  ArtifactsScreen,
  ComponentsScreen,
  JourneyGateScreen,
  BacklogScreen,
  CyclesScreen,
  DeliveryCheckScreen,
  ContextAuditScreen,
  HistoryScreen,
  SquadsScreen,
  JourneyScreen,
  DesignerScreen,
  GateAScreen,
  GateNegocioScreen,
  GateSquadsScreen,
  GateEscopoScreen,
  GateFinalScreen,
  GateBScreen,
  GatesQueueScreen,
  LiveBoardScreen,
  MissionControlScreen,
  AgentRoomGeralScreen,
  AgentSuggestScreen,
  AgentObservabilityScreen,
  OutputScreen,
  FailureScreen,
} from '@/screens';

// Títulos e subtítulos das telas — espelho do protótipo
const SCREEN_META = {
  dashboard:    { title: 'Dashboard',              subtitle: 'Visão geral dos projetos do tenant' },
  usage:        { title: 'Custos',                 subtitle: 'Consumo e custo de IA do tenant (tokens + US$)' },
  assistant:    { title: 'Assistente',             subtitle: 'Chat com o Claude — resposta em streaming (token a token)' },
  project:      { title: 'Gestão do Projeto',      subtitle: 'Mapa da jornada, Quality Gates, auditoria e qualidade' },
  workflows:    { title: 'Squads',                 subtitle: 'Templates de squads' },
  catalog:      { title: 'Catálogo de Agents',     subtitle: 'Sub-agents e skills disponíveis' },
  agentRoom:    { title: 'Sala dos Agentes',       subtitle: 'Atividade ao vivo de todos os agentes do tenant' },
  agentObservability: { title: 'Observabilidade de Agentes', subtitle: 'Comportamento, custo, tempo e erros de cada agente por projeto' },
  agentSuggest: { title: 'Sugestão de Agentes',    subtitle: 'Analisa o escopo e sugere o squad de agentes (por tipo de entrega)' },
  grafo:        { title: 'Grafo do Repositório',    subtitle: 'Grafo de conhecimento do código (Graphify)' },
  history:      { title: 'Histórico',              subtitle: 'Execuções anteriores' },
  contextAudit: { title: 'Auditoria de Contexto',  subtitle: 'O que o gerador realmente tem por projeto' },
  settings:     { title: 'Configurações',          subtitle: 'Tenant, vendors e segurança' },
  accessMatrix: { title: 'Matriz de Acessos',      subtitle: 'Permissões e associação de usuários' },
  stepPrompts:  { title: 'Prompts das Jornadas',   subtitle: 'Prompt-base por etapa da jornada' },
  journey:      { title: 'Jornada do Projeto',     subtitle: 'Jornada de 6 fases' },
  designStudio: { title: 'Design Studio',          subtitle: 'Fase 2 · Escopo de Negócio' },
  designer:     { title: 'Squad Designer',         subtitle: 'Fase 4 · Definição dos Squads' },
  gateNegocio:  { title: 'Quality Gate 1',         subtitle: 'Escopo de Negócio' },
  gateTecnologia:{ title: 'Quality Gate 2',        subtitle: 'Escopo de Tecnologia' },
  gateSquads:   { title: 'Quality Gate 3',         subtitle: 'Composição dos Squads' },
  gateEscopo:   { title: 'Quality Gate 4',         subtitle: 'Escopo & Ciclos de Desenvolvimento' },
  gates:        { title: 'Aprovação de Gates',     subtitle: 'Quality Gates pendentes' },
  gateB:        { title: 'Quality Gate B',         subtitle: 'Plano de execução · Ciclo de desenvolvimento' },
  gateFinal:    { title: 'Quality Gate 5 — Final', subtitle: 'Aceite da entrega · Negócio + Técnica' },
  liveboard:    { title: 'Live Board',             subtitle: 'Fase 6 · Execução & Revisão' },
  missionControl:{ title: 'Mission Control',        subtitle: 'Trace, agentes e topologia da execução' },
  deliveryCheck:{ title: 'Conferência de Entrega', subtitle: 'Pedido vs. entregue · rastreabilidade e aceite' },
  failure:      { title: 'Execução com falha',     subtitle: 'Diagnóstico e plano de ação' },
  output:       { title: 'Output',                 subtitle: 'Resultado da execução' },
};

/**
 * NegocioGateRoute — /gates/negocio serve DOIS contextos:
 *   - SEM ?execution= → Gate 1 · Negócio do wizard (journey_gates, GateNegocioScreen).
 *   - COM ?execution= → Gate A da EXECUÇÃO (GateAScreen): aprova via
 *     PATCH /executions/{id}/gates/a, que envia o SIGNAL ao workflow Temporal e
 *     destrava a execução. Sem isto, o "Revisar →" do Live Board caía no
 *     GateNegocioScreen (journey_gate) — aprovava mas NÃO avançava a execução.
 */
function NegocioGateRoute() {
  const [searchParams] = useSearchParams();
  return searchParams.get('execution') ? <GateAScreen /> : <GateNegocioScreen />;
}

// Telas que já renderizam o próprio <h1>: o topo não repete o título (visual Sphere).
const TELAS_COM_TITULO_PROPRIO = new Set([
  'dashboard', 'missionControl', 'accessMatrix', 'settings', 'stepPrompts', 'gateB',
  'failure', 'output', 'gates', 'grafo', 'journey',
]);

/** Wrapper que injeta AppLayout com os metadados corretos da tela atual. */
function AppShell({ screen, children }) {
  const { navigate } = useNavigationContext();
  const meta = SCREEN_META[screen] ?? {};
  const sidebarActive =
    screen === 'accessMatrix' || screen === 'stepPrompts' ? 'settings' :
    ['project', 'workflows', 'catalog', 'grafo', 'history', 'contextAudit', 'settings',
      'missionControl', 'agentRoom', 'agentObservability', 'usage'].includes(screen)
      ? screen
      : 'dashboard';

  return (
    <AppLayout
      currentScreen={sidebarActive}
      navigate={navigate}
      title={meta.title}
      subtitle={meta.subtitle}
      hideTitle={TELAS_COM_TITULO_PROPRIO.has(screen)}
      scrollKey={screen}
    >
      {children}
    </AppLayout>
  );
}

/** Tela placeholder reutilizável para rotas ainda não portadas. */
function NotYetPorted({ screenName, description }) {
  return (
    <PlaceholderScreen screenName={screenName} description={description} />
  );
}

/**
 * ProtectedShell — combina AuthGuard + AppShell.
 * Todas as rotas autenticadas usam este wrapper.
 */
function ProtectedShell({ screen, requireAdmin = false, children }) {
  return (
    <AuthGuard requireAdmin={requireAdmin}>
      <AppShell screen={screen}>
        {children}
      </AppShell>
    </AuthGuard>
  );
}

/** Árvore de rotas — dentro do NavigationProvider */
function AppRoutes() {
  return (
    <Routes>
      {/* Abertura — público: animação 3D de I.A. → /login (fora do AuthGuard) */}
      <Route path="/" element={<SplashScreen />} />

      {/* Login — público, sem AuthGuard nem AppLayout */}
      <Route path="/login" element={<LoginScreen />} />

      {/* Retorno do SSO (Microsoft Entra ID) — público: é justamente aqui que a
          sessão é estabelecida, então não pode estar atrás do AuthGuard. */}
      <Route path="/auth/callback" element={<SsoCallbackScreen />} />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedShell screen="dashboard">
            <DashboardScreen />
          </ProtectedShell>
        }
      />

      {/* Custos — consumo/custo de IA (app.wallet_usage) */}
      <Route
        path="/usage"
        element={
          <ProtectedShell screen="usage">
            <UsageScreen />
          </ProtectedShell>
        }
      />

      {/* Assistente — chat com streaming (SSE) */}
      <Route
        path="/assistant"
        element={
          <ProtectedShell screen="assistant">
            <AssistantScreen />
          </ProtectedShell>
        }
      />

      {/* Gestão de Projetos — F0-FE-3 */}
      <Route
        path="/project"
        element={
          <ProtectedShell screen="project">
            <ProjectHubScreen />
          </ProtectedShell>
        }
      />

      {/* Squads — F0-FE-3 */}
      <Route
        path="/workflows"
        element={
          <ProtectedShell screen="workflows">
            <SquadsScreen />
          </ProtectedShell>
        }
      />

      {/* Catálogo — F1-FE-5: portado, ligado a GET /agents */}
      <Route
        path="/catalog"
        element={
          <ProtectedShell screen="catalog">
            <CatalogScreen />
          </ProtectedShell>
        }
      />

      {/* Sala dos Agentes — visão geral ao vivo (GET /agents/activity) */}
      <Route
        path="/agents/room"
        element={
          <ProtectedShell screen="agentRoom">
            <AgentRoomGeralScreen />
          </ProtectedShell>
        }
      />

      {/* Observabilidade de Agentes — custo, tempo, erros e loops por agente (GET /observability/*) */}
      <Route
        path="/agents/observabilidade"
        element={
          <ProtectedShell screen="agentObservability">
            <AgentObservabilityScreen />
          </ProtectedShell>
        }
      />

      {/* Sugestão de Agentes — squad por escopo + tipo de entrega (POST /agents/suggest) */}
      <Route
        path="/agents/sugestao"
        element={
          <ProtectedShell screen="agentSuggest">
            <AgentSuggestScreen />
          </ProtectedShell>
        }
      />

      {/* Grafo do Repositório (Graphify) — viz do grafo de conhecimento */}
      <Route
        path="/grafo"
        element={
          <ProtectedShell screen="grafo">
            <GraphViewScreen />
          </ProtectedShell>
        }
      />

      {/* Histórico — F0-FE-3 */}
      <Route
        path="/history"
        element={
          <ProtectedShell screen="history">
            <HistoryScreen />
          </ProtectedShell>
        }
      />

      {/* Auditoria de Contexto — o que o codegen tem por projeto + rastro da execução */}
      <Route
        path="/context-audit"
        element={
          <ProtectedShell screen="contextAudit">
            <ContextAuditScreen />
          </ProtectedShell>
        }
      />

      {/* Configurações — F0-FE-3: portado, ligado a GET /settings */}
      <Route
        path="/settings"
        element={
          <ProtectedShell screen="settings">
            <SettingsScreen />
          </ProtectedShell>
        }
      />
      {/* Matriz de Acessos — F0-FE-3: portado, ligado aos endpoints de access-matrix */}
      <Route
        path="/settings/access-matrix"
        element={
          <ProtectedShell screen="accessMatrix" requireAdmin={false}>
            <AccessMatrixScreen />
          </ProtectedShell>
        }
      />
      {/* Prompts das Jornadas — tela dedicada (irmã do card "Tokens por Step") */}
      <Route
        path="/settings/step-prompts"
        element={
          <ProtectedShell screen="stepPrompts" requireAdmin={false}>
            <StepPromptsScreen />
          </ProtectedShell>
        }
      />

      {/* Jornada — F1-FE-1: portado */}
      <Route
        path="/journey"
        element={
          <ProtectedShell screen="journey">
            <JourneyScreen />
          </ProtectedShell>
        }
      />
      {/* Callback OAuth Git (popup) — PÚBLICO (sem AuthGuard): devolve code+state
          ao opener via postMessage e fecha. A troca por token roda no opener. */}
      <Route path="/auth/git/callback" element={<GitOAuthCallback />} />
      <Route
        path="/journey/design-studio"
        element={
          <ProtectedShell screen="designStudio">
            <DesignStudioScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/journey/blueprint"
        element={
          <ProtectedShell screen="project">
            <BlueprintScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/journey/cycles"
        element={
          <ProtectedShell screen="project">
            <CyclesScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/journey/backlog"
        element={
          <ProtectedShell screen="project">
            <BacklogScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/journey/artifacts"
        element={
          <ProtectedShell screen="project">
            <ArtifactsScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/journey/components"
        element={
          <ProtectedShell screen="project">
            <ComponentsScreen />
          </ProtectedShell>
        }
      />
      {/* /journey/data-tech desativado (item 15 · Data · Tecnologia fora de uso) */}
      <Route
        path="/journey/designer"
        element={
          <ProtectedShell screen="designer">
            <DesignerScreen />
          </ProtectedShell>
        }
      />

      {/* Gates — F2-FE-2: fila portada + Gate A portado */}
      <Route
        path="/gates"
        element={
          <ProtectedShell screen="gates">
            <GatesQueueScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/gates/negocio"
        element={
          <ProtectedShell screen="gateNegocio">
            <NegocioGateRoute />
          </ProtectedShell>
        }
      />
      {/* Gate B — F3-FE-2: portado, plano de tasks + decisão via PATCH /gates/b */}
      <Route
        path="/gates/b"
        element={
          <ProtectedShell screen="gateB">
            <GateBScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/gates/tecnologia"
        element={
          <ProtectedShell screen="gateTecnologia">
            <JourneyGateScreen gateKey="tecnologia" title="Quality Gate 2 — Tecnologia" />
          </ProtectedShell>
        }
      />
      <Route
        path="/gates/squads"
        element={
          <ProtectedShell screen="gateSquads">
            <GateSquadsScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/gates/escopo"
        element={
          <ProtectedShell screen="gateEscopo">
            <GateEscopoScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/gates/final"
        element={
          <ProtectedShell screen="gateFinal">
            <GateFinalScreen />
          </ProtectedShell>
        }
      />

      {/* Live Board — F2-FE-3: portado, consumindo SSE via fetch+ReadableStream */}
      <Route
        path="/execution/liveboard"
        element={
          <ProtectedShell screen="liveboard">
            <LiveBoardScreen />
          </ProtectedShell>
        }
      />
      {/* Mission Control — monitoramento transversal (trace/agentes/topologia) */}
      <Route
        path="/execution/mission-control"
        element={
          <ProtectedShell screen="missionControl">
            <MissionControlScreen />
          </ProtectedShell>
        }
      />
      <Route
        path="/execution/delivery-check"
        element={
          <ProtectedShell screen="deliveryCheck">
            <DeliveryCheckScreen />
          </ProtectedShell>
        }
      />
      {/* Failure — F3-FE-3: portado, consumindo GET /executions/{id}/output */}
      <Route
        path="/execution/failure"
        element={
          <ProtectedShell screen="failure">
            <FailureScreen />
          </ProtectedShell>
        }
      />
      {/* Output — F3-FE-3: portado, 4 abas via GET /executions/{id}/output */}
      <Route
        path="/execution/output"
        element={
          <ProtectedShell screen="output">
            <OutputScreen />
          </ProtectedShell>
        }
      />

      {/* Catch-all → dashboard (o AuthGuard redireciona para /login se não autenticado) */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/** Raiz do roteador — inclui NavaStyles + NavigationProvider */
export function AppRouter() {
  return (
    <>
      <NavaStyles />
      <NavigationProvider>
        <AppRoutes />
      </NavigationProvider>
    </>
  );
}
